'use client';

import { useAppNavigation } from '@/hooks/useAppNavigation';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

interface ViewMoreButtonProps {
  groupId: string;
  deviceType: EPlatform;
  onExposure?: () => void;
  onClick?: () => void;
}

// VIEW MORE按钮组件 - 与Vue版本完全一致
export default function ViewMoreButton({
  groupId,
  deviceType,
  onExposure,
  onClick,
}: ViewMoreButtonProps) {
  const { navigate } = useAppNavigation();
  const tButtons = useTranslations('activity.pinkLeopardGlasses.buttons');

  const { ref, inView } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理曝光埋点
  useEffect(() => {
    if (inView && onExposure) {
      onExposure();
    }
  }, [inView, onExposure]);

  // 处理点击跳转
  const handleViewMoreClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      // 触发点击埋点
      onClick?.();

      // 设置进入商品列表的来源标识
      try {
        sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
      } catch {
        // 忽略存储失败
      }

      // 使用navigate方法跳转到商品列表页
      navigate(`/goods-list/${groupId}`);
    },
    [onClick, navigate, groupId],
  );

  const isWeb = deviceType === EPlatform.WEB;

  return (
    <Link
      ref={ref}
      href={`/goods-list/${groupId}`}
      className={`view-more flex items-center justify-center font-bb text-white cursor-pointer transition-all duration-200 hover:opacity-90 no-underline ${
        isWeb
          ? `
            md:w-[250px] md:h-[40px] md:text-[12px] md:rounded-[20px] md:my-[13px]
            lg:w-[333px] lg:h-[53px] lg:text-[16px] lg:rounded-[27px] lg:my-[17px]
            xl:w-[417px] xl:h-[67px] xl:text-[20px] xl:rounded-[33px] xl:my-[21px]
            2xl:w-[450px] 2xl:h-[72px] 2xl:text-[22px] 2xl:rounded-[36px] 2xl:my-[23px]
            3xl:w-[500px] 3xl:h-[80px] 3xl:text-[24px] 3xl:rounded-[40px] 3xl:my-[25px] 3xl:mb-[90px]
            bg-[#463ae8] mx-auto`
          : `w-[250px] h-[40px] bg-[#463ae8] rounded-[20px] text-[12px] mx-auto`
      }`}
      aria-label={tButtons('viewMore')}
      onClick={handleViewMoreClick}
    >
      {tButtons('viewMore')}
    </Link>
  );
}
